import {
  makeStyles,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelectedTag } from "../../contexts/SelectedTagContext";
import { useTagsRearrange } from "../../hooks/useTags";
import { FlatVsaaTag, RearrangeTagRequest } from "../../models/VsaaTag";
import {
  getParentIdFromAncestors,
  hasDuplicateNameAtSameLevel,
} from "../../services/TagService";
import { TreeTagPickerDialog } from "../tagpicker/TreeTagPickerDialog";

interface RearrangeTagsPanelProps {
  tagCollectionId: string;
  allTags: FlatVsaaTag[];
  onIsRearranging: (isRearranging: boolean) => void;
}

const useStyles = makeStyles({
  rearrangeContainer: {
    display: "flex",
    flexDirection: "column",
  },
  rearrangeTitle: {
    marginBottom: "10px",
  },
});

const RearrangeTagsPanel = (props: RearrangeTagsPanelProps) => {
  const { tagCollectionId, allTags, onIsRearranging } = props;
  const tagContext = useSelectedTag();
  const classes = useStyles();
  const { mutateAsync: rearrangeTag } = useTagsRearrange(tagCollectionId);
  const { t } = useTranslation();
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  const handlePickerSubmit = async (selectedTags: FlatVsaaTag[]) => {
    if (!tagContext?.selectedTag) return;
    const { selectedTag } = tagContext;
    const selectedParent = selectedTags[0] || null;

    // Get the target parent ID
    const targetParentId = selectedParent
      ? getParentIdFromAncestors(
          selectedParent.ancestorIds
            ? selectedParent.ancestorIds[
                selectedParent.ancestorIds.length - 1
              ] === selectedParent.id
              ? selectedParent.ancestorIds
              : [...selectedParent.ancestorIds, selectedParent.id]
            : [selectedParent.id]
        )
      : undefined;

    const isDuplicate = hasDuplicateNameAtSameLevel(
      selectedTag.title,
      targetParentId,
      allTags,
      selectedTag.id
    );

    if (isDuplicate) {
      setDuplicateError(t("duplicateTagNameError"));
      return;
    }

    setDuplicateError(null);

    const rearrangeRequest: RearrangeTagRequest = {
      tags: [{ id: selectedTag.id, targetPosition: undefined }],
      fromAncestorIds: selectedTag.ancestorIds
        ? selectedTag.ancestorIds[selectedTag.ancestorIds.length - 1] ===
          selectedTag.id
          ? selectedTag.ancestorIds
          : [...selectedTag.ancestorIds, selectedTag.id]
        : [selectedTag.id],
      targetAncestorIds: selectedParent.ancestorIds
        ? selectedParent.ancestorIds[selectedParent.ancestorIds.length - 1] ===
          selectedParent.id
          ? selectedParent.ancestorIds
          : [...selectedParent.ancestorIds, selectedParent.id]
        : [selectedParent.id],
    };

    onIsRearranging(true);
    await rearrangeTag(rearrangeRequest);
    onIsRearranging(false);
  };

  return (
    <div className={classes.rearrangeContainer}>
      <h3 className={classes.rearrangeTitle}>{t("structureLocation")}</h3>
      <div>
        <TreeTagPickerDialog
          tagCollectionId={tagCollectionId}
          label={t("moveTagDialog")}
          multiValue={false}
          values={[]}
          allTags={allTags}
          onSubmit={handlePickerSubmit}
          rearrange={true}
        />
        {duplicateError && (
          <MessageBar intent="error">
            <MessageBarBody>{duplicateError}</MessageBarBody>
          </MessageBar>
        )}
      </div>
    </div>
  );
};

export default RearrangeTagsPanel;
